'use client'

import Image from '@/components/Image'
import { ChevronEnd } from '@/components/icons/Chevron'
import { useTranslation } from '@/contexts/I18nContext'
import type { MemoryItem } from '@/types'
import type { CharacterChatViewData } from '@/types/characterChat'
import { type CharacterMemoriesPreview, useCharacterMemories } from './useCharacterMemories'

interface CharacterMemoriesCardProps {
  character: CharacterChatViewData
  /** pc: 固定 px（角色信息卡）；mobile: vw 适配（角色详情页） */
  variant: 'pc' | 'mobile'
}

interface CardRenderProps {
  character: CharacterChatViewData
  preview: CharacterMemoriesPreview
  openPlaza: () => void
  openDetail: (item: MemoryItem) => void
}

/** 角色 Memories 入口卡：按平台拆分为独立 UI，尺寸各自固定。 */
export function CharacterMemoriesCard({ character, variant }: CharacterMemoriesCardProps) {
  const { preview, openPlaza, openDetail } = useCharacterMemories(character)

  if (variant === 'mobile') {
    return (
      <MemoriesCardMobile
        character={character}
        preview={preview}
        openPlaza={openPlaza}
        openDetail={openDetail}
      />
    )
  }

  return (
    <MemoriesCardPc
      character={character}
      preview={preview}
      openPlaza={openPlaza}
      openDetail={openDetail}
    />
  )
}

/** 记忆区标题装饰图标（PC 固定 8px） */
function PcSectionMark() {
  return (
    <Image
      src="/comments/star.png"
      alt=""
      width={16}
      height={16}
      className="size-[8px] shrink-0 object-contain"
    />
  )
}

/** 平台共用：用户头像 + 链接图标 + 角色头像（PC 固定 px） */
function PcAvatarStack({
  item,
  character,
}: {
  item: MemoryItem
  character: CharacterChatViewData
}) {
  return (
    <span className="flex shrink-0 items-center">
      <Image
        src={item.creator?.avatar || '/common/defaultAvatar.png'}
        alt=""
        width={32}
        height={32}
        className="size-[24px] rounded-full border border-white/40 object-cover"
      />
      <Image
        src="/memory/MemoryAvatarLinkIcon.png"
        alt=""
        width={16}
        height={16}
        className="size-[16px] rounded-full bg-black/20 object-contain"
        style={{ marginInlineStart: '4px', marginInlineEnd: '4px' }}
      />
      <Image
        src={item.npc?.avatar || character.avatar}
        alt=""
        width={32}
        height={32}
        className="size-[24px] rounded-full border border-white/40 object-cover"
      />
    </span>
  )
}

/** PC 单条记忆预览行 */
function PcMemoryPreviewRow({
  item,
  character,
  onClick,
}: {
  item: MemoryItem
  character: CharacterChatViewData
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-[8px] text-start"
    >
      <PcAvatarStack item={item} character={character} />

      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] leading-[18px] text-white/[0.87]">
          {item.title}
        </span>
      </span>

      <span className="flex w-[24px] shrink-0 flex-col items-center gap-[2px] text-white/[0.6]">
        <Image
          src="/profile/web/character-chat.svg"
          alt=""
          width={16}
          height={16}
          className="size-[16px] object-contain"
        />
        <span className="text-center text-[10px] leading-none">{item.messageCount || 0}</span>
      </span>
    </button>
  )
}

/** PC 入口卡：固定 px */
export function MemoriesCardPc({ character, preview, openPlaza, openDetail }: CardRenderProps) {
  const { t } = useTranslation('profile')

  if (preview.records.length === 0) {
    return (
      <button
        type="button"
        onClick={openPlaza}
        className="mt-[12px] flex w-full items-center justify-between rounded-[8px] bg-white/[0.12] p-[12px] text-start"
      >
        <span className="flex items-center gap-[8px]">
          <PcSectionMark />
          <span className="text-[14px] leading-[20px] font-normal text-white/[0.87]">
            {t('Profile.TabMemory')}
          </span>
        </span>
        <span className="flex items-center gap-[4px] text-[14px] leading-[20px] font-normal text-white/[0.38]">
          {t('Profile.Web.Empty.Memories')}
          <ChevronEnd className="size-[16px]" />
        </span>
      </button>
    )
  }

  return (
    <section className="mt-[12px] flex w-full flex-col gap-[12px] rounded-[8px] bg-white/[0.12] p-[12px]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[8px]">
          <PcSectionMark />
          <h2 className="text-[14px] leading-[18px] font-normal text-white/[0.87]">
            {t('Profile.TabMemory')}
          </h2>
        </div>
        {preview.total > 0 ? (
          <button
            type="button"
            onClick={openPlaza}
            className="flex items-center gap-[4px] text-[14px] leading-[20px] font-normal text-white"
          >
            {t('Profile.Web.Memory.ViewAll', { values: { count: preview.total } })}
            <ChevronEnd className="size-[16px]" />
          </button>
        ) : null}
      </div>

      <div className="h-[2px] w-full bg-white/40" />

      <div className="flex flex-col gap-[12px]">
        {preview.records.slice(0, 3).map((item) => (
          <PcMemoryPreviewRow
            key={item.id}
            item={item}
            character={character}
            onClick={() => openDetail(item)}
          />
        ))}
      </div>
    </section>
  )
}

/** 记忆区标题装饰图标（移动端 8vw） */
function MobileSectionMark() {
  return (
    <Image
      src="/comments/star.png"
      alt=""
      width={16}
      height={16}
      className="size-[calc(8*var(--vw))] shrink-0 object-contain"
    />
  )
}

/** 平台共用：用户头像 + 链接图标 + 角色头像（移动端按 vw 缩放） */
function MobileAvatarStack({
  item,
  character,
}: {
  item: MemoryItem
  character: CharacterChatViewData
}) {
  return (
    <span className="flex shrink-0 items-center">
      <Image
        src={item.creator?.avatar || '/common/defaultAvatar.png'}
        alt=""
        width={32}
        height={32}
        className="size-[calc(24*var(--vw))] rounded-full border border-white/40 object-cover"
      />
      <Image
        src="/memory/MemoryAvatarLinkIcon.png"
        alt=""
        width={16}
        height={16}
        className="size-[calc(16*var(--vw))] rounded-full bg-black/20 object-contain"
        style={{
          marginInlineStart: 'calc(4*var(--vw))',
          marginInlineEnd: 'calc(4*var(--vw))',
        }}
      />
      <Image
        src={item.npc?.avatar || character.avatar}
        alt=""
        width={32}
        height={32}
        className="size-[calc(24*var(--vw))] rounded-full border border-white/40 object-cover"
      />
    </span>
  )
}

/** 移动端单条记忆预览行 */
function MobileMemoryPreviewRow({
  item,
  character,
  onClick,
}: {
  item: MemoryItem
  character: CharacterChatViewData
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-[calc(8*var(--vw))] text-start"
    >
      <MobileAvatarStack item={item} character={character} />

      <span className="min-w-0 flex-1">
        <span className="block truncate text-[calc(12.8*var(--vw))] leading-[calc(16*var(--vw))] text-white/[0.87]">
          {item.title}
        </span>
      </span>

      <span className="flex w-[calc(24*var(--vw))] shrink-0 flex-col items-center gap-[calc(2*var(--vw))] text-white/[0.6]">
        <Image
          src="/profile/web/character-chat.svg"
          alt=""
          width={16}
          height={16}
          className="size-[calc(16*var(--vw))] object-contain"
        />
        <span className="text-center text-[calc(9.6*var(--vw))] leading-none">
          {item.messageCount || 0}
        </span>
      </span>
    </button>
  )
}

/** 移动端入口卡：vw 适配 */
export function MemoriesCardMobile({ character, preview, openPlaza, openDetail }: CardRenderProps) {
  const { t } = useTranslation('profile')

  if (preview.records.length === 0) {
    return (
      <button
        type="button"
        onClick={openPlaza}
        className="mx-[calc(16*var(--vw))] flex w-[calc(343*var(--vw))] items-center justify-between rounded-[8px] bg-white/[0.12] p-[calc(12*var(--vw))] text-start"
      >
        <span className="flex items-center gap-[calc(8*var(--vw))]">
          <MobileSectionMark />
          <span className="text-[calc(14*var(--vw))] leading-[calc(20*var(--vw))] font-normal text-white/[0.87]">
            {t('Profile.TabMemory')}
          </span>
        </span>
        <span className="flex items-center gap-[calc(4*var(--vw))] text-[calc(14*var(--vw))] leading-[calc(20*var(--vw))] font-normal text-white/[0.38]">
          {t('Profile.Web.Empty.Memories')}
          <ChevronEnd className="size-[calc(16*var(--vw))]" />
        </span>
      </button>
    )
  }

  return (
    <section className="mx-[calc(16*var(--vw))] flex w-[calc(343*var(--vw))] flex-col gap-[calc(12*var(--vw))] rounded-[8px] bg-white/[0.12] p-[calc(12*var(--vw))]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[calc(8*var(--vw))]">
          <MobileSectionMark />
          <h2 className="text-[calc(14*var(--vw))] leading-[calc(18*var(--vw))] font-normal text-white/[0.87]">
            {t('Profile.TabMemory')}
          </h2>
        </div>
        {preview.total > 0 ? (
          <button
            type="button"
            onClick={openPlaza}
            className="flex items-center gap-[calc(4*var(--vw))] text-[calc(14*var(--vw))] leading-[calc(20*var(--vw))] font-normal text-white"
          >
            {t('Profile.Web.Memory.ViewAll', { values: { count: preview.total } })}
            <ChevronEnd className="size-[calc(16*var(--vw))]" />
          </button>
        ) : null}
      </div>

      <div className="h-px w-full bg-white/40" />

      <div className="flex flex-col gap-[calc(12*var(--vw))]">
        {preview.records.slice(0, 3).map((item) => (
          <MobileMemoryPreviewRow
            key={item.id}
            item={item}
            character={character}
            onClick={() => openDetail(item)}
          />
        ))}
      </div>
    </section>
  )
}
